import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { Navigate, useLocation } from "react-router-dom"
import { ApiError, type Company, type Employee, type MyCompany, api, companyStore, lastCompanyStore, setUnauthorizedHandler, tokenStore } from "./api"

// Session state, and the guard in front of the authenticated routes.
//
// The token lives in localStorage so a refresh does not sign the user out, and
// the employee and company are re-fetched on load rather than persisted — a
// stale role or credit balance cached in the browser is worse than a request.

type Session = { employee: Employee; company: Company }

type AuthValue = {
  session: Session | null
  /** Distinguishes "not signed in" from "we do not know yet", which decides
   *  whether the guard redirects or waits. */
  loading: boolean
  signIn: (token: string) => Promise<void>
  signOut: () => void
  refresh: () => Promise<void>
  /** Act as another company. /auth/me then answers as the chosen company, so
   *  the whole shell — nav, balance, role — follows. */
  switchCompany: (companyId: string) => Promise<void>
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!tokenStore.get()) {
      setSession(null)
      setLoading(false)
      return
    }
    // In several companies with none chosen (409 company_required): land in
    // the one last used in this browser, else the first — the sidebar switcher
    // is right there to change it. Staff with no membership can act for any
    // company, so they land in the first of all of them.
    const pickOrFail = async (error: unknown) => {
      if (!(error instanceof ApiError) || error.status !== 409) throw error
      const list = await api.get<{ companies: MyCompany[]; staff: boolean }>("/auth/me/companies")
      const options =
        list.companies.length > 0 || !list.staff
          ? list.companies
          : (await api.get<{ companies: MyCompany[] }>("/platform/companies")).companies
      const last = lastCompanyStore.get()
      const pick = options.find((c) => c.id === last) ?? options[0]
      if (!pick) throw error
      companyStore.set(pick.id)
      setSession(await api.get<Session>("/auth/me"))
    }
    try {
      try {
        setSession(await api.get<Session>("/auth/me"))
      } catch (error) {
        // Acting as a company that no longer resolves (deleted, or access lost)
        // must not read as an expired login. Drop the acting state and ask the
        // API again without it.
        if (!companyStore.get() || (error instanceof ApiError && error.status === 409)) throw error
        companyStore.clear()
        setSession(await api.get<Session>("/auth/me"))
      }
    } catch (error) {
      try {
        await pickOrFail(error)
      } catch {
        // api.ts has already cleared the token on a 401. Anything else here
        // means we cannot establish a session either way.
        setSession(null)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  // Remembered on every load, so whatever the session settled on — a switch,
  // a pick, a single company — is where this browser lands next time.
  useEffect(() => {
    if (session) lastCompanyStore.set(session.company.id)
  }, [session])

  useEffect(() => {
    // Any 401 from anywhere ends the session, so an expired token cannot leave
    // the UI rendering a shell around requests that will all fail.
    setUnauthorizedHandler(() => setSession(null))
    void load()
  }, [load])

  const value = useMemo<AuthValue>(
    () => ({
      session,
      loading,
      signIn: async (token: string) => {
        tokenStore.set(token)
        setLoading(true)
        await load()
      },
      signOut: () => {
        tokenStore.clear()
        // Otherwise the next person on this browser starts in someone else's
        // acting-as state.
        companyStore.clear()
        setSession(null)
      },
      refresh: load,
      switchCompany: async (companyId: string) => {
        companyStore.set(companyId)
        await load()
      },
    }),
    [session, loading, load],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext)
  if (!value) throw new Error("useAuth must be used inside AuthProvider")
  return value
}

export function useSession(): Session {
  const { session } = useAuth()
  // Only ever rendered inside RequireAuth, which does not render children
  // without a session.
  if (!session) throw new Error("useSession used outside an authenticated route")
  return session
}

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth()
  const location = useLocation()

  // Redirecting while the session is still loading would sign out anyone who
  // refreshes the page.
  if (loading) return <FullPageSpinner />
  if (!session) return <Navigate to="/login" replace state={{ from: location.pathname }} />
  return <>{children}</>
}

/** Moderniser staff only. RequireAdmin admits a customer's own admin, who must
 *  never reach a surface that spans tenants or shows the cost basis. */
export function RequirePlatformAdmin({ children }: { children: React.ReactNode }) {
  const { employee } = useSession()
  if (employee?.role !== "platform_admin") return <Navigate to="/" replace />
  return <>{children}</>
}

export function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { employee } = useSession()
  const isAdmin = employee.role === "admin" || employee.role === "platform_admin"
  // Sent to the dashboard rather than shown a refusal: the nav does not offer
  // these links to a developer, so arriving here means a typed URL.
  if (!isAdmin) return <Navigate to="/" replace />
  return <>{children}</>
}

/** The external role has developer-level visibility everywhere except
 *  workspaces and sessions — the API answers 403 for those, so the web app
 *  only redirects a typed URL to match. */
export function DenyExternal({ children }: { children: React.ReactNode }) {
  const { employee } = useSession()
  if (employee?.role === "external") return <Navigate to="/" replace />
  return <>{children}</>
}

export function FullPageSpinner() {
  return (
    <div className="flex h-full items-center justify-center">
      <div
        className="size-6 animate-spin rounded-full border-2 border-border border-t-accent"
        role="status"
        aria-label="Loading"
      />
    </div>
  )
}

